export const OTP_LENGTH = 6;
/**
 * How long a verification code lives. luce-backend's
 * `ClientVerifyPhoneNumber#expire_and_create_pin_code` sets `5.minutes`, and
 * refuses a resend until the code expires. Used when the server's expiry
 * can't be read.
 */
export const OTP_CODE_LIFETIME_SECONDS = 5 * 60;

// Ruby's `Time#to_s` ("2026-10-26 21:34:13 UTC") or ISO 8601 ("…T21:34:13Z")
const SERVER_TIME = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?\s*(?:UTC|Z)$/;

/**
 * Whole seconds until a code expires, from the server's `expiresAt` and this
 * device's clock. `undefined` when it can't be read or lands outside a
 * plausible window, so the caller falls back to the code's full lifetime. A
 * code never has more left than it lives: more means this device's clock is
 * behind the server's.
 */
export function otpSecondsLeft(
  expiresAt: string | undefined,
  now: number = Date.now(),
): number | undefined {
  const match = expiresAt ? SERVER_TIME.exec(expiresAt.trim()) : null;
  if (!match) return undefined;
  const [year, month, day, hour, minute, second] = match.slice(1, 7).map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const milliseconds = Number((match[7] ?? "").padEnd(3, "0").slice(0, 3));
  const time = Date.UTC(year, month - 1, day, hour, minute, second, milliseconds);
  const date = new Date(time);
  // Date.UTC rolls an impossible date over (Feb 30 → Mar 2) instead of failing
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day ||
    date.getUTCHours() !== hour ||
    date.getUTCMinutes() !== minute ||
    date.getUTCSeconds() !== second
  ) {
    return undefined;
  }
  const seconds = Math.ceil((time - now) / 1000);
  return seconds > 0 && seconds <= OTP_CODE_LIFETIME_SECONDS ? seconds : undefined;
}

const SEPARATOR = "・";

export function joinParts(...parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(SEPARATOR);
}

export function withLeadingPlus(phoneNumber: string): string {
  return phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
}

export function isPinCodeStillActive(message: string): boolean {
  return /still active/i.test(message);
}

export function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}
